import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface StreamSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartStream: (title: string, category: string) => void;
  initialTitle?: string;
}

const StreamSetupModal: React.FC<StreamSetupModalProps> = ({
  isOpen,
  onClose,
  onStartStream,
  initialTitle = ''
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(initialTitle);
  const [category, setCategory] = useState('just-chatting');
  const [isCameraReady, setIsCameraReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const categories = [
    { id: 'just-chatting', icon: '💬', label: 'Sohbet' },
    { id: 'gaming', icon: '🎮', label: 'Oyun' },
    { id: 'music', icon: '🎵', label: 'Müzik' },
    { id: 'irl', icon: '🚶', label: 'IRL' },
    { id: 'creative', icon: '🎨', label: 'Yaratıcı' },
  ];

  useEffect(() => {
    let stream: MediaStream | null = null;

    if (isOpen) {
      // Kamerayı başlat
      const startCamera = async () => {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: { ideal: 1280, max: 1920 },
              height: { ideal: 720, max: 1080 }
            },
            audio: true
          });
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            setIsCameraReady(true);
          }
        } catch (err) {
          console.error("Camera access denied:", err);
          // Fallback or error handling can go here
        }
      };

      startCamera();
    } else {
      // Temizle
      if (videoRef.current && videoRef.current.srcObject) {
        const tracks = (videoRef.current.srcObject as MediaStream).getTracks();
        tracks.forEach(track => track.stop());
      }
      setIsCameraReady(false);
    }

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [isOpen]);

  const handleStart = () => {
    if (!title.trim()) return;
    onStartStream(title, category);
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.container}>


        <h2 style={styles.heading}>Yayına Hazır Mısın?</h2>

        <div style={styles.previewContainer}>
          {isCameraReady ? (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={styles.videoPreview}
            />
          ) : (
            <div style={styles.placeholderPreview}>
              🎥 Kamera başlatılıyor...
            </div>
          )}
          <div style={styles.liveBadge}>CANLI ÖNİZLEME</div>
        </div>

        <div style={styles.formContainer}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Yayın Başlığı</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Bugün neler yapıyoruz?"
              style={styles.input}
              maxLength={60}
            />
            <div style={styles.charCount}>{title.length}/60</div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Kategori</label>
            <div style={styles.categoryGrid}>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setCategory(cat.id)}
                  style={{
                    ...styles.categoryButton,
                    ...(category === cat.id ? styles.categoryButtonActive : {})
                  }}
                >
                  <span style={styles.catIcon}>{cat.icon}</span>
                  <span style={styles.catLabel}>{cat.label}</span>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleStart}
            disabled={!title.trim()}
            style={{
              ...styles.startButton,
              ...(title.trim() ? {} : styles.startButtonDisabled)
            }}
          >
            YAYINI BAŞLAT
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backdropFilter: 'blur(5px)',
  },
  container: {
    backgroundColor: '#1a1a1a',
    borderRadius: '24px',
    padding: '24px',
    width: '100%',
    maxWidth: '450px',
    color: 'white',
    position: 'relative',
    boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
    border: '1px solid rgba(255,255,255,0.1)',
  },

  heading: {
    textAlign: 'center',
    fontSize: '24px',
    fontWeight: '800',
    margin: '0 0 24px 0',
    background: 'linear-gradient(45deg, #ff007f, #ff9800)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  previewContainer: {
    width: '100%',
    aspectRatio: '9/16',
    maxHeight: '300px',
    backgroundColor: '#000',
    borderRadius: '16px',
    overflow: 'hidden',
    marginBottom: '24px',
    position: 'relative',
    margin: '0 auto',
  },
  videoPreview: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  placeholderPreview: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#666',
  },
  liveBadge: {
    position: 'absolute',
    top: '12px',
    left: '12px',
    backgroundColor: 'rgba(0,0,0,0.6)',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 'bold',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.2)',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    position: 'relative',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#aaa',
  },
  input: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    padding: '16px',
    color: 'white',
    fontSize: '16px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  charCount: {
    position: 'absolute',
    right: '12px',
    top: '40px',
    fontSize: '12px',
    color: '#666',
  },
  categoryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '8px',
  },
  categoryButton: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    padding: '12px 8px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    color: '#ccc',
    transition: 'all 0.2s',
  },
  categoryButtonActive: {
    backgroundColor: 'rgba(255, 0, 127, 0.2)',
    borderColor: '#ff007f',
    color: '#ff007f',
  },
  catIcon: {
    fontSize: '20px',
  },
  catLabel: {
    fontSize: '12px',
    fontWeight: '500',
  },
  startButton: {
    background: 'linear-gradient(45deg, #ff007f, #ff9800)',
    border: 'none',
    borderRadius: '12px',
    padding: '16px',
    color: 'white',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '8px',
    boxShadow: '0 4px 12px rgba(255, 0, 127, 0.3)',
    transition: 'transform 0.1s, box-shadow 0.2s',
  },
  startButtonDisabled: {
    background: '#333',
    color: '#666',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
};

export default StreamSetupModal;

